import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { PERM } from '#/config/permissions';
import { useCan } from '#/modules/infra/auth/useCan';
import { fetchInventoryStockDetail } from '#/api/imsMgmt/inventoryStock/fetchDetail';
import { splitInventoryStock } from '#/api/imsMgmt/inventoryStock/split';
import { fetchMtrStructTree } from '#/api/imsMgmt/settings/mtrStruct/fetchTree';
import useSideDetail from '#/hooks/useSideDetail';
import { Button } from '#/modules/commonComponents/button';
import SideMsg from '#/modules/commonComponents/SideMsg';
import { createAsyncFilterLabelCache } from '#/modules/commonComponents/table/asyncFilterLabelCache';
import ListTablePanel from '#/modules/commonComponents/table/ListTablePanel';
import { buildFilterStatusFieldsFromColumns } from '#/modules/commonComponents/table/buildFilterStatusFields';
import { mergeFilterUpdate } from '#/modules/commonComponents/table/listFilterKeys';
import { applyQtyFilters } from '#/modules/commonComponents/table/qtyFilterHelpers';
import { createSelectColumn } from '#/modules/commonComponents/table/tableColumnHelpers';
import PageShell from '#/modules/infra/layout/PageShell';
import SideMsgLayout from '#/modules/infra/layout/SideMsgLayout';
import InventoryStockPrintConfirmDialog from '#/modules/imsMgmt/inventoryStock/components/InventoryStockPrintConfirmDialog';
import InventoryStockSplitDialog from '#/modules/imsMgmt/inventoryStock/components/InventoryStockSplitDialog';
import { buildInventoryStockColumns } from '#/modules/imsMgmt/inventoryStock/data/inventoryStockColumns';
import { inventoryStockSideMsgBlocks } from '#/modules/imsMgmt/inventoryStock/data/inventoryStockSideMsgConfig';
import {
  getInventoryStockApiErrorMessage,
  isInventoryStockVersionConflict,
} from '#/modules/imsMgmt/inventoryStock/helper/apiError';
import { buildPrintConfirmItems } from '#/modules/imsMgmt/inventoryStock/helper/buildPrintConfirmItems';
import useInventoryStockList from '#/modules/imsMgmt/inventoryStock/hooks/useInventoryStockList';
import usePrintInventoryStockLabels from '#/modules/imsMgmt/inventoryStock/hooks/usePrintInventoryStockLabels';
import { fromTree } from '#/modules/imsMgmt/settings/mtrStruct/helper/structFilterModel';
import { hydrateWarehouseFilterLabels } from '#/modules/imsMgmt/settings/storePlace/helper/warehouseSelectOptions';
import {
  hydrateWarehouseLocationFilterLabels,
} from '#/modules/imsMgmt/settings/storePlace/helper/warehouseLocationSelectOptions';
import {
  inventoryStockListQueryKeyPrefix,
  mtrStructTreeQueryKey,
} from '#/utils/queryKeys';
import { getRequestErrorMessage } from '#/utils/requestError';
import { toast } from '#/utils/toast';

function canSplitInventoryStock(row) {
  return row?.status === 'ACTIVE' && row?.invStatus === 'AVAILABLE';
}

function resolveInventoryStockUpdatedAt(stock) {
  return stock?.record?.updatedAt ?? stock?.updatedAt ?? null;
}

const INVENTORY_STOCK_SORT_FIELD_LABELS = {
  stockNo: '庫存編號',
  mtrNo: '料號',
  mtrName: '名稱',
  status: '狀態',
  invStatus: '庫存狀態',
  warehouseId: '倉別',
  locationId: '儲位',
};

const QTY_FILTER_STATUS_FIELDS = [
  {
    key: 'baseQtyGt',
    label: '數量 > 0',
    resolveDisplay: () => ['是'],
  },
  { key: 'baseQtyMin', label: '最小數量' },
  { key: 'baseQtyMax', label: '最大數量' },
];

function selectedIdsFromRowSelection(selection) {
  return Object.keys(selection ?? {}).filter((id) => selection[id]);
}

const InventoryStockListPage = () => {
  const canSplit = useCan(PERM.INVENTORY_STOCK_SPLIT);
  const [selectedIds, setSelectedIds] = useState([]);
  const [printConfirmOpen, setPrintConfirmOpen] = useState(false);
  const [printConfirmKey, setPrintConfirmKey] = useState(0);
  const [printConfirmItems, setPrintConfirmItems] = useState([]);
  const [splitOpen, setSplitOpen] = useState(false);
  const [splitDialogKey, setSplitDialogKey] = useState(0);
  const [warehouseLabelCache] = useState(createAsyncFilterLabelCache);
  const [locationLabelCache] = useState(createAsyncFilterLabelCache);
  const [labelCacheVersion, setLabelCacheVersion] = useState(0);
  const listRef = useRef(null);
  const queryClient = useQueryClient();
  const { displayData, setSelectedData } = useSideDetail();

  const printMutation = usePrintInventoryStockLabels({
    onSuccess: () => {
      setPrintConfirmOpen(false);
      listRef.current?.clearSelection?.();
      setSelectedIds([]);
    },
  });

  const splitMutation = useMutation({
    mutationFn: async ({ id, splits, stock }) => {
      let updatedAt = resolveInventoryStockUpdatedAt(stock);
      if (!updatedAt) {
        const detail = await fetchInventoryStockDetail(id);
        updatedAt = resolveInventoryStockUpdatedAt(detail?.data);
      }
      if (!updatedAt) {
        const missingError = new Error('MISSING_UPDATED_AT');
        missingError.code = 'MISSING_UPDATED_AT';
        throw missingError;
      }
      return splitInventoryStock(id, { splits, updatedAt });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: inventoryStockListQueryKeyPrefix(),
      });
    },
    onError: (error) => {
      if (error?.code === 'MISSING_UPDATED_AT') {
        toast.error('缺少版本時間，請重新載入後再試');
        return;
      }
      toast.error(getInventoryStockApiErrorMessage(error, '拆分失敗，請稍後再試'));
      if (isInventoryStockVersionConflict(error)) {
        queryClient.invalidateQueries({
          queryKey: inventoryStockListQueryKeyPrefix(),
        });
      }
    },
  });

  const isPrinting = printMutation.isPending;
  const isSplitting = splitMutation.isPending;

  const {
    rows,
    pagination,
    isLoading,
    isError,
    error,
    refetch,
    listQuery,
    setPage,
    setPageSize,
    setKeyword,
    setFilter,
    patchFilters,
    setSort,
    removeSortAt,
    resetListQuery,
  } = useInventoryStockList();

  useEffect(() => {
    let cancelled = false;

    Promise.all([
      hydrateWarehouseFilterLabels(listQuery.filters?.warehouseId, warehouseLabelCache),
      hydrateWarehouseLocationFilterLabels(listQuery.filters?.locationId, locationLabelCache),
    ]).then(([didWh, didLoc]) => {
      if (!cancelled && (didWh || didLoc)) {
        setLabelCacheVersion((version) => version + 1);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [
    listQuery.filters?.warehouseId,
    listQuery.filters?.locationId,
    warehouseLabelCache,
    locationLabelCache,
  ]);

  const treeQuery = useQuery({
    queryKey: mtrStructTreeQueryKey(),
    queryFn: () => fetchMtrStructTree({ status: 'ACTIVE' }),
  });

  const structModel = useMemo(
    () => fromTree(treeQuery.data?.data ?? []),
    [treeQuery.data?.data],
  );

  const handleFilterChange = useCallback((key, value) => {
    if (key === 'warehouseId') {
      patchFilters((prev) => {
        let next = mergeFilterUpdate(prev, 'warehouseId', value);
        next = mergeFilterUpdate(next, 'locationId', undefined);
        return next;
      });
      return;
    }
    setFilter(key, value);
  }, [patchFilters, setFilter]);

  const handleQtyFiltersChange = useCallback((draft) => {
    patchFilters((prev) => {
      const result = applyQtyFilters(prev, draft);
      if (!result.ok) {
        toast.error('最小數量不可大於最大數量');
        return prev;
      }
      return result.filters;
    });
  }, [patchFilters]);

  const handleSortingChange = useCallback((sorting) => {
    setSort((sorting ?? []).map((item) => ({
      id: item.id,
      desc: Boolean(item.desc),
    })));
  }, [setSort]);

  const columns = useMemo(
    () => {
      void labelCacheVersion;
      return [
        createSelectColumn({ enableMultiSelection: true }),
        ...buildInventoryStockColumns({
          filters: listQuery.filters || {},
          onFilterChange: handleFilterChange,
          onQtyFiltersChange: handleQtyFiltersChange,
          warehouseLabelCache,
          locationLabelCache,
        }),
      ];
    },
    [
      listQuery.filters,
      handleFilterChange,
      handleQtyFiltersChange,
      warehouseLabelCache,
      locationLabelCache,
      labelCacheVersion,
    ],
  );

  const filterStatusFields = useMemo(
    () => [
      ...buildFilterStatusFieldsFromColumns(columns, { structModel }),
      ...QTY_FILTER_STATUS_FIELDS,
    ],
    [columns, structModel],
  );

  const handleRowClick = useCallback((row, isSelected) => {
    if (isSelected) {
      setSelectedData(row.original);
      return;
    }

    setSelectedData(null);
  }, [setSelectedData]);

  const handleRowSelectionChange = useCallback((selection) => {
    setSelectedIds(selectedIdsFromRowSelection(selection));
  }, []);

  const handleOpenPrintConfirm = useCallback(() => {
    if (isPrinting) {
      return;
    }
    const selectedRows = listRef.current?.getSelectedRows?.() ?? [];
    const ids = selectedRows
      .map((row) => (row?.id == null ? '' : String(row.id)))
      .filter(Boolean);
    if (ids.length === 0) {
      return;
    }
    setPrintConfirmItems(buildPrintConfirmItems(selectedRows, ids));
    setPrintConfirmKey((prev) => prev + 1);
    setPrintConfirmOpen(true);
  }, [isPrinting]);

  const handlePrintConfirm = useCallback((request) => {
    const ids = request?.invStockIds ?? [];
    if (!ids.length || isPrinting) {
      return;
    }
    printMutation.mutate({
      invStockIds: ids,
      unitSelections: request.unitSelections ?? [],
    });
  }, [isPrinting, printMutation]);

  const handlePrintSingle = useCallback(() => {
    const id = displayData?.id;
    if (!id || isPrinting) {
      return;
    }
    setPrintConfirmItems(buildPrintConfirmItems([displayData], [String(id)]));
    setPrintConfirmKey((prev) => prev + 1);
    setPrintConfirmOpen(true);
  }, [displayData, isPrinting]);

  const handleOpenSplit = useCallback(() => {
    if (!canSplit || !canSplitInventoryStock(displayData) || isSplitting) {
      return;
    }
    setSplitDialogKey((prev) => prev + 1);
    setSplitOpen(true);
  }, [canSplit, displayData, isSplitting]);

  const handleSplitConfirm = useCallback(async (splits) => {
    const id = displayData?.id;
    if (!id || !splits?.length || isSplitting) {
      return null;
    }
    return splitMutation.mutateAsync({ id: String(id), splits, stock: displayData });
  }, [displayData, isSplitting, splitMutation]);

  const sideMsgBlocks = useMemo(() => [
    {
      condition: (row) => Boolean(row?.id),
      fields: [
        {
          key: 'stockActions',
          render: (row) => (
            <div className="side_msg-row">
              <h3>操作</h3>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  disabled={isPrinting}
                  onClick={handlePrintSingle}
                >
                  {isPrinting ? '列印中…' : '列印本筆'}
                </Button>
                {canSplit && canSplitInventoryStock(row) ? (
                  <Button
                    type="button"
                    disabled={isSplitting}
                    onClick={handleOpenSplit}
                  >
                    {isSplitting ? '拆分中…' : '拆分'}
                  </Button>
                ) : null}
              </div>
            </div>
          ),
        },
      ],
    },
    ...inventoryStockSideMsgBlocks,
  ], [canSplit, handleOpenSplit, handlePrintSingle, isPrinting, isSplitting]);

  const headerAction = (
    <Button
      type="button"
      disabled={selectedIds.length === 0 || isPrinting}
      onClick={handleOpenPrintConfirm}
    >
      {isPrinting ? '列印中…' : `列印選取標籤${selectedIds.length > 0 ? `（${selectedIds.length}）` : ''}`}
    </Button>
  );

  return (
    <PageShell variant="listWithSide">
      <div className="layout-shell__body flex min-h-0 flex-col">
        <h1 className="page_title shrink-0">
          <span className="border-l-5 border-primary-400 pl-2.5">庫存</span>
        </h1>
        <SideMsgLayout>
          <div className="table-panel min-h-0 xl:h-full">
            <ListTablePanel
              ref={listRef}
              tableKey="ims.inventory_stock.list"
              columns={columns}
              rows={rows}
              pagination={pagination}
              isLoading={isLoading}
              isError={isError}
              errorMessage={isError ? getRequestErrorMessage(error, '載入列表失敗') : undefined}
              onRetry={isError ? () => { refetch(); } : undefined}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              onRowClick={handleRowClick}
              onSearch={setKeyword}
              onReset={resetListQuery}
              initialKeyword={listQuery.keyword}
              searchPlaceholder="搜尋庫存編號、料號或名稱"
              headerAction={headerAction}
              listQuery={listQuery}
              filterStatusFields={filterStatusFields}
              sortFieldLabels={INVENTORY_STOCK_SORT_FIELD_LABELS}
              onFilterChange={handleFilterChange}
              onKeywordClear={() => setKeyword('')}
              onSortRemove={removeSortAt}
              onSortingChange={handleSortingChange}
              enableMultiSelection
              onRowSelectionChange={handleRowSelectionChange}
            />
          </div>

          <SideMsg
            title="庫存"
            titleValue="stockNo"
            displayData={displayData}
            blocks={sideMsgBlocks}
          />
        </SideMsgLayout>
      </div>

      <InventoryStockPrintConfirmDialog
        key={printConfirmKey}
        open={printConfirmOpen}
        onOpenChange={setPrintConfirmOpen}
        items={printConfirmItems}
        isPrinting={isPrinting}
        onConfirm={handlePrintConfirm}
      />

      <InventoryStockSplitDialog
        key={`${displayData?.id || 'split'}-${splitDialogKey}`}
        open={splitOpen}
        onOpenChange={setSplitOpen}
        stock={displayData}
        isPending={isSplitting}
        onConfirm={handleSplitConfirm}
      />
    </PageShell>
  );
};

export default InventoryStockListPage;
